import { setShowSuccess } from 'actions/ui';
import { ArrayHelpers, Copy, Forms, Loading, SaveButton, SystemText } from 'e4shared';
import { getPeriodStart, getValidFor, periodName, periodTrans, sortGoalsByPeriod } from 'helpers/goals';
import { hasModule } from 'helpers/profile';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { apiGet, apiPatch } from 'services';
import { CalcAutoValue, CalculatePIRating } from '../rating';
import { addDays, addWeeks, addMonths, e4Format, e4Formats } from 'helpers/dates';
import { systemTranslationTypes, translateSys } from 'helpers/translations';

export const Intro = ({
    appraisal,
    profile,
    setPage,
    state,
    setGoals,
    goals,
    predictions,
    setPredictions,
    setInReview,
    inReview,
    pages,
    inPreview,
    rate,
    next,
}) => {
    const dispatch = useDispatch();

    const [availableGoals, setAvailableGoals] = React.useState([]);
    const [assignedGoals, setAssignedGoals] = React.useState([]);
    const [errors, setErrors] = React.useState({});
    const [error, setError] = React.useState(false);
    const [numericError, setNumericError] = React.useState(false);
    const [values, setValues] = React.useState({});
    const [loading, setLoading] = React.useState(false);
    const [saving, setSaving] = React.useState(false);

    const user = useSelector(state => state.user.user);

    React.useEffect(() => {
        setLoading(true);
        apiGet(`quota-values/${profile.id}`, user.guid, response => {
            let temp = sortGoalsByPeriod(response);
            setAvailableGoals(temp);
            setLoading(false);
        });
    }, [profile.id]);

    React.useEffect(() => {
        if (goals) {
            const temp = {};
            goals.forEach(g => {
                temp[`goal${g.quota_Id}`] = g.value;
            });
            setValues(temp);
        }
    }, [goals]);

    let copy = state.user
        ? translateSys(
              user.language_Code,
              systemTranslationTypes.reviewUser,
              user.companySettings,
              SystemText('Reviews/Appraisals/Wizard/Paragraphs/Intro/You', profile?.first_Name)
          )
        : translateSys(
              user.language_Code,
              systemTranslationTypes.reviewManager,
              user.companySettings,
              SystemText('Reviews/Appraisals/Wizard/Paragraphs/Intro/Them', profile?.first_Name)
          );

    copy = copy.replace('[FIRSTNAME]', profile?.first_Name);
    copy = copy.replace('{0}', profile?.first_Name);

    const loadAssignedGoals = () => {
        let temp = [...availableGoals];

        if (state.readonly && appraisal) {
            const pastGoals = JSON.parse(appraisal.goals || '{}');
            temp = temp.map(t => ({ ...t, this_Target: pastGoals[`goal${t.id}`] }));
        }

        setAssignedGoals(temp);
    };

    const save = callback => {
        if (inPreview) {
            callback();
            return;
        }

        setSaving(true);
        const goals = assignedGoals.map(assignedGoal => {
            const goal = {
                quota_Id: assignedGoal.id,
                valid_For: getValidFor(assignedGoal.period, user.fy_Start_Date),
                value: values[`goal${assignedGoal.id}`],
            };
            return goal;
        });

        apiPatch(`review-goals/${appraisal.id}`, goals, user.guid, () => {
            setSaving(false);
            setGoals(goals);
            callback(goals);
            // hasModule(user, 'integrations') &&
            //     ArrayHelpers.existsAndHasLength(assignedGoals) &&
            //     dispatch(setShowSuccess(SystemText('Reviews/Appraisals/Wizard/Goals/Success')));
        });
    };

    React.useEffect(() => {
        loadAssignedGoals();
    }, [profile?.id, appraisal?.id, availableGoals]);

    const onChange = (x, prop) => {
        if (x > 999999999) return;
        const temp = { ...values };
        temp[`goal${prop}`] = x;
        setValues(temp);
    };

    const validate = () => {
        if (inPreview) return true;

        let valid = true;
        setError(false);
        setNumericError(false);
        const temp = {};

        hasModule(user, 'integrations') &&
            ArrayHelpers.existsAndHasLength(assignedGoals) &&
            assignedGoals.forEach(g => {
                values[`goal${g.id}`] = values[`goal${g.id}`] || g.this_Target;
                if (!values[`goal${g.id}`]) {
                    temp[`goal${g.id}`] = true;
                    setError(true);
                    valid = false;
                } else if (
                    isNaN(values[`goal${g.id}`]) ||
                    (g.format !== 'P' && values[`goal${g.id}`].toString().indexOf('.') >= 0)
                ) {
                    temp[`goal${g.id}`] = true;
                    setNumericError(true);
                    valid = false;
                }
            });

        setErrors(temp);

        !valid && setSaving(false);

        return valid;
    };

    const per = SystemText('Reviews/Appraisals/Wizard/Goals/Per');
    const placeholder = x => SystemText('Reviews/Appraisals/Wizard/Goals/Placeholder', x);
    const seconds = SystemText('Goals/Historic/Seconds');
    const goalError = SystemText('Reviews/Appraisals/Wizard/Goals/Error');
    const goalNumeric = SystemText('Reviews/Appraisals/Wizard/Goals/Numeric');

    if (loading) return <Loading />;

    const name = goal => (goal.format === 'S' ? goal.name + ` (${seconds})` : goal.name);

    const reCalculatePositiveIndicators = goals => {
        if (state.readonly) return;
        const temp = { ...predictions };

        appraisal.competencies?.forEach(competency => {
            temp[competency.id.toString()] = temp[competency.id.toString()] || {};
            let comp = { ...temp[competency.id.toString()], id: competency.id };
            let hasPIGoal = false;
            (competency.positiveIndicators || []).forEach((pi, piIdx) => {
                if (pi.goal) {
                    hasPIGoal = true;
                    const autoValue = CalcAutoValue(user, goals, pi);
                    comp.positiveIndicators[(piIdx + 1).toString()] = autoValue;
                    comp.userPositiveIndicators[(piIdx + 1).toString()] = autoValue;
                    comp.managerPositiveIndicators[(piIdx + 1).toString()] = autoValue;
                }
            });

            if (hasPIGoal) {
                const level = CalculatePIRating(competency, predictions) || comp.level;
                const preds = state.user ? comp.userPositiveIndicators : comp.managerPositiveIndicators;
                rate(comp, level, preds);
            }
        });
    };

    if (
        !state.past &&
        !(
            (appraisal.participants.indexOf('U') >= 0 && state.user) ||
            (appraisal.participants.indexOf('M') >= 0 && state.manager) ||
            inPreview
        )
    ) {
        return <div className="text-center">{SystemText('Reviews/Appraisals/Wizard/ZeroOther', profile.first_Name)}</div>;
    }

    return (
        <>
            <div>
                <div className="uppercase">
                    {SystemText('Reviews/Appraisals/Wizard/Scheduled')} {e4Format(appraisal.scheduled)}
                </div>
                <h2>
                    {state.user
                        ? SystemText('Reviews/Appraisals/Wizard/Headings/H2/Review/Title/User')
                        : SystemText('Reviews/Appraisals/Wizard/Headings/H2/Review/Title/Profile', profile.first_Name)}
                </h2>
                <p>
                    <Copy value={copy} />
                </p>
            </div>
            {hasModule(user, 'integrations') && ArrayHelpers.existsAndHasLength(assignedGoals) && (
                <>
                    <hr style={{ margin: '20px 0' }} />
                    <div className="flex mb20">
                        <div className="pt10">
                            <i className="fa fa-warning fa-lg text-danger mr10" />
                        </div>
                        <div>
                            {state.user ? (
                                <div>{SystemText('Reviews/Appraisals/Wizard/Rating/NoGoalsSet/You')}</div>
                            ) : (
                                <div>
                                    {SystemText('Reviews/Appraisals/Wizard/Rating/NoGoalsSet/Them', profile.full_Name)}
                                </div>
                            )}
                            <div className="mt10">{SystemText('Reviews/Appraisals/Wizard/Goals/Range/Intro')}</div>
                            {assignedGoals.some(g => g.period === 'Q') && (
                                <div className="mt10">
                                    <Copy
                                        value={SystemText(
                                            'Reviews/Appraisals/Wizard/Goals/Range/Quarterly',
                                            e4Format(addMonths(getPeriodStart('Q', user.fy_Start_Date), -3), e4Formats.week),
                                            e4Format(addDays(getPeriodStart('Q', user.fy_Start_Date), -1), e4Formats.week)
                                        )}
                                    />
                                </div>
                            )}
                            {assignedGoals.some(g => g.period === 'M') && (
                                <div className="mt10">
                                    <Copy
                                        value={SystemText(
                                            'Reviews/Appraisals/Wizard/Goals/Range/Monthly',
                                            e4Format(addMonths(getPeriodStart('M'), -3), e4Formats.week),
                                            e4Format(addDays(getPeriodStart('M'), -1), e4Formats.week)
                                        )}
                                    />
                                </div>
                            )}
                            {assignedGoals.some(g => g.period === 'W') && (
                                <div className="mt10">
                                    <Copy
                                        value={SystemText(
                                            'Reviews/Appraisals/Wizard/Goals/Range/Weekly',
                                            e4Format(addWeeks(getPeriodStart('W'), -4), e4Formats.week),
                                            e4Format(addDays(getPeriodStart('W'), -1), e4Formats.week)
                                        )}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                    {(error || numericError) && (
                        <div className="flex flex-center">
                            <div className="flex-30" />
                            <div className="flex-grow text-danger pr0 pl10 pb10">{error ? goalError : goalNumeric}</div>
                        </div>
                    )}
                    <div className="mb20 review-goals">
                        {assignedGoals.map((goal, i) => {
                            if (values[`goal${goal.id}`] === undefined) values[`goal${goal.id}`] = goal.this_Target;
                            return (
                                <div className="flex flex-center mb10 inline-form" key={`goal-${goal.id}`}>
                                    <div className="flex-30 mr10 text-right">
                                        <label>
                                            {name(goal)}
                                            <span className="mandatory" title={SystemText('General/Mandatory')}>
                                                *
                                            </span>
                                        </label>
                                    </div>
                                    <div className="flex-grow">
                                        <Forms.Text
                                            readonly={state.readonly}
                                            data-testid="input-goal-value"
                                            error={errors[`goal${goal.id}`]}
                                            value={
                                                goal.format === 'P'
                                                    ? 100 * values[`goal${goal.id}`]
                                                    : values[`goal${goal.id}`]
                                            }
                                            onChange={x => onChange(goal.format === 'P' ? x / 100 : x, goal.id)}
                                            numeric
                                            placeholder={placeholder(periodName(goal.period, false).toLowerCase())}
                                        />
                                    </div>
                                    <div className="flex-30 ml10">
                                        {per} {periodTrans(goal.period)}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
            <div className="text-center mt20">
                {state.readonly ? (
                    <button
                        className="btn btn-success"
                        data-testid="button-appraisal-review"
                        onClick={() => setPage(pages.length + 1)}
                    >
                        {SystemText('Reviews/Appraisals/Wizard/Buttons/ReviewPredictions')}
                    </button>
                ) : (
                    <SaveButton
                        key="footer-begin"
                        saving={saving}
                        setSaving={setSaving}
                        className="btn btn-success"
                        onClick={() =>
                            validate() &&
                            save(goals => {
                                setTimeout(() => {
                                    reCalculatePositiveIndicators(goals);
                                    next(inReview);
                                    setInReview(false);
                                }, 250);
                            })
                        }
                        data-testid="button-appraisal-begin"
                    >
                        {inReview
                            ? SystemText('Reviews/Appraisals/Wizard/Buttons/UpdateGoals')
                            : SystemText('Reviews/Appraisals/Wizard/Buttons/BeginRating')}
                    </SaveButton>
                )}
            </div>
        </>
    );
};

export default Intro;
